import {FrmShapePoint} from "./FrmShapePoint";
import {Container} from "pixi.js";
import {Subscription} from "nate-react-api-helpers";
import {FrmCalcPoint} from "./FrmCalcPoint";

let adjustmentId = 0;
const DEBUG = false;

// FrmRefPoint links one shape with another within a scene.
// A ref point describes an anchor that is either:
//   - local:  backed by a FrmShapePoint that can move as the shape is edited
//   - static: a fixed point on a graphic ({graphic, x, y})
// Use linkTo() to make another ref point's graphic track this one.
export class FrmRefPoint {

    // dependents that follow this anchor (this == driver)
    refs: Reference[] = [];
    group: FrmRefPointGroup | null = null;

    // either point may be used to define the local position
    calculatedPoint: FrmCalcPoint | null = null;
    localPoint: FrmShapePoint | null = null;
    staticPoint: {
        graphic: Container;
        x: number;
        y: number,
        name: string
    } | null = null;

    private localSub: Subscription | null = null;

    constructor(opt?: {
        name: string;
        graphic: Container;
        staticX: number;
        staticY: number;
    }) {
        if(opt) {
            this.staticPoint = {
                graphic: opt.graphic,
                x: opt.staticX,
                y: opt.staticY,
                name: opt.name,
            };
        }

        if(FrmRefPoint.autoLinkGroup) {
            this.group = FrmRefPoint.autoLinkGroup;
            this.group.add(this);
        }
    }

    setLocalPoint(localPoint: FrmShapePoint) {
        if(this.staticPoint !== null || this.calculatedPoint !== null) {
            throw new Error("FrmRefPoint: cannot set localPoint when staticPoint or calculatedPoint is already set");
        }

        this.localPoint = localPoint;

        // when the local point moves, reposition any dependents that follow it
        this.localSub?.cancel();
        this.localSub = localPoint.changed.subscribe((ev) => this.adjustOthers(ev.name, ev.adjustmentId));
    }

    setCalculatedPoint(calc: FrmCalcPoint) {
        if(this.staticPoint !== null || this.localPoint !== null) {
            throw new Error("FrmRefPoint: cannot set calculatedPoint when staticPoint or localPoint is already set");
        }

        this.calculatedPoint = calc;

        // when the calculated point moves, reposition any dependents that follow it
        // this.localSub?.cancel();
        // this.localSub = calc.changed.subscribe((ev) => this.adjustOthers(ev.name, ev.adjustmentId));
    }

    // link this anchor to `other` so the two stay at a fixed offset (offsetX,
    // offsetY). the link is BIDIRECTIONAL: adjustOthers() may be called on any
    // node, and that node becomes the source of truth that every connected node
    // realigns to. we store the edge in both directions (forward on `other`,
    // reversed on `this`) so the constraint graph can be walked from either end.
    linkTo(other: FrmRefPoint, offsetX: number, offsetY: number) {
        if(this.localPoint === null && this.staticPoint === null && this.calculatedPoint === null) {
            throw new Error("FrmRefPoint: linkTo requires a localPoint, calculatedPoint or staticPoint");
        }

        // the offset is given in `other`'s local units. Both edges keep it that way
        // (`frame`), so a walk from either end turns and scales it the same.
        other.refs.push({ref: this, offsetX, offsetY, frame: other});
        this.refs.push({
            ref: other,
            offsetX: -offsetX,
            offsetY: -offsetY,
            frame: other,
        });

        // position this graphic immediately to establish the link. ongoing
        // updates are driven by other.localPoint's changed subscription (set up
        // in other.setLocalPoint), which also calls other.adjustOthers().
        other.adjustOthers("linkTo(" + this.debugName() + "," + other.debugName() + ")");
    }

    lastAdjustmentId: number = 0;

    // mark this anchor and every other point in its rigid body (group) as visited
    // for this adjustment pass. points in a group share a graphic and move as a
    // unit, so locking one must lock all of them. note this only sets the points'
    // lastAdjustmentId (the "don't reposition me" flag); it deliberately leaves
    // group.lastAdjustmentId alone so the group still propagates its members'
    // dependents exactly once (see the group block in _adjustOther2).
    private lockBody(adjustmentId: number) {
        this.lastAdjustmentId = adjustmentId;

        if(this.group) {
            const pts = this.group.refPoints;
            for(let i = 0; i < pts.length; i++) {
                pts[i].lastAdjustmentId = adjustmentId;
            }
        }
    }

    adjustOthers(debugName: string, adjustId?: number) {
        if(!adjustId) {
            adjustId = newAdjustmentId()
        }

        if(DEBUG) console.log("adjustOthers:begin", debugName, adjustId);

        this._adjustOthers(adjustId, debugName);

        if(DEBUG) console.log("adjustOthers:end", debugName, adjustId);
    }

    // reposition every dependent graphic so its anchor sits at this anchor + offset
    private _adjustOthers(adjustmentId: number, from: string, debugIndent = 0, debugExtra?: string) {
        // lock our whole rigid body before propagating. a body (a group of points
        // sharing one graphic, e.g. a door's 4 corners) moves as a unit, so once
        // any one of its points has been placed the body is fixed. marking the
        // entire group here is what makes the origin the immovable source of truth
        // AND stops a later back-edge from re-positioning a sibling point (which
        // would silently drag the shared graphic and compound every call).
        this.lockBody(adjustmentId);

        const g = this.getGraphic();
        const lx = this.getLocalX();
        const ly = this.getLocalY();
        if(g) {
            // local points live in the graphic's own coordinate space, so they go
            // through its local transform (scale and rotation) to land in scene (parent)
            // space. The group layout (FrmSceneLayout) scales linked graphics to fit, and
            // a turned run (the top-down section at an angled mullion) rotates them —
            // ignoring either makes dependents jump on re-link.
            const m = localMatrix(g);
            const baseX = m.a * lx + m.c * ly + m.tx;
            const baseY = m.b * lx + m.d * ly + m.ty;

            this._adjustOther2(adjustmentId, baseX, baseY, m, debugIndent);
        } else {
            // fallback for non-graphic items
            this._adjustOther2(adjustmentId, lx, ly, IDENTITY, debugIndent)
        }
    }

    private _adjustOther2(adjustmentId: number, baseX: number, baseY: number, m: LinearMap, debugIndent: number) {
        for (let i = 0; i < this.refs.length; i++) {
            const ref = this.refs[i];

            if(ref.ref.lastAdjustmentId === adjustmentId) continue;
            // lock the dependent's whole body the instant we reach it, so the
            // rest of this pass treats it as placed and never moves it again.
            ref.ref.lockBody(adjustmentId);

            const debugAddr = this.debugName() + "->" + ref.ref.debugName();
            if(DEBUG) console.log("\t".repeat(debugIndent)+ "adjust", adjustmentId, debugAddr);

            const rg = ref.ref.getGraphic();
            if (rg) {
                // the offset is expressed in the local units of the graphic it was
                // linked in (it scales and turns with that graphic — the driver's on a
                // forward edge, the dependent's own on a reversed one); the dependent's
                // local anchor goes through the dependent's scale and rotation. Graphics
                // have no pivot, so the position is the local origin's place in the parent.
                const rm = localMatrix(rg);
                const fm = ref.frame === this ? m : ref.frame.linearMap();
                const rlx = ref.ref.getLocalX();
                const rly = ref.ref.getLocalY();
                const offX = fm.a * ref.offsetX + fm.c * ref.offsetY;
                const offY = fm.b * ref.offsetX + fm.d * ref.offsetY;
                const anchorX = rm.a * rlx + rm.c * rly;
                const anchorY = rm.b * rlx + rm.d * rly;

                const x = baseX + offX - anchorX;
                const y = baseY + offY - anchorY;

                if(DEBUG) console.log("\t".repeat(debugIndent)+ "adjust", adjustmentId, ref.ref.debugName() + ` to ${printPoint(x, y)} - was ${printPoint(rg.position.x, rg.position.y)}`)
                if(DEBUG) console.log("\t".repeat(debugIndent)+ "adjust", adjustmentId, `${printPoint(baseX, baseY)} + ${printPoint(offX, offY)} - ${printPoint(anchorX, anchorY)}`)

                rg.position.set(x, y);
            }

            ref.ref._adjustOthers(adjustmentId, this.debugName(), debugIndent+1);
        }

        if(this.group) {
            if(this.group.lastAdjustmentId !== adjustmentId) {
                this.group.lastAdjustmentId = adjustmentId;

                this.group.refPoints.forEach(p => {
                    p._adjustOthers(adjustmentId, "group", debugIndent + 1);
                });
            }
        }
    }

    debugName() {
        if(this.localPoint) {
            return this.localPoint.shape.name;
        }

        if(this.calculatedPoint) {
            return this.calculatedPoint.name;
        }

        return this.staticPoint?.name || "(none)";
    }

    getLocalX() {
        if(this.isLocalMode()) {
            return this.localPoint?.x || 0;
        }

        if(this.calculatedPoint) {
            return this.calculatedPoint.x || 0;
        }

        return this.staticPoint?.x || 0;
    }

    getLocalY() {
        if(this.isLocalMode()) {
            return this.localPoint?.y || 0;
        }

        if(this.calculatedPoint) {
            return this.calculatedPoint.y || 0;
        }

        return this.staticPoint?.y || 0;
    }

    // the local transform of this point's graphic; identity for a non-graphic point.
    private linearMap(): LinearMap {
        const g = this.getGraphic();
        return g ? localMatrix(g) : IDENTITY;
    }

    getGraphic(): Container | undefined {
        if(this.isLocalMode()) {
            return this.localPoint?.getGraphic();
        }

        if(this.calculatedPoint) {
            return this.calculatedPoint.graphic;
        }

        return this.staticPoint?.graphic;
    }

    isLocalMode() {
        return this.localPoint !== null;
    }

    static autoLinkGroup: FrmRefPointGroup | null = null;

    static buildGroup(param: () => void) {
        if(this.autoLinkGroup) {
            throw new Error("called buildGroup nested");
        }

        try {
            this.autoLinkGroup = new FrmRefPointGroup();
            param();
        } finally {
            this.autoLinkGroup = null;
        }
    }

    // join already-constructed points into one rigid body, for points a class declares
    // as fields (so they exist before any buildGroup could wrap them). A point can
    // belong to one body only.
    static groupExisting(points: FrmRefPoint[]) {
        const group = new FrmRefPointGroup();
        for(const pt of points) {
            if(pt.group) {
                throw new Error("FrmRefPoint: groupExisting called on a point that already has a group");
            }
            pt.group = group;
            group.add(pt);
        }
    }
}

class FrmRefPointGroup {
    refPoints: FrmRefPoint[] = [];
    lastAdjustmentId: number = 0;

    add(param: FrmRefPoint) {
        this.refPoints.push(param);
    }
}

function newAdjustmentId() {
    if(adjustmentId > 500000) {
        adjustmentId = 0;
    }

    return adjustmentId++;
}

type Reference = {
    ref: FrmRefPoint;
    offsetX: number;
    offsetY: number;
    // the point whose graphic's local units the offset is expressed in.
    frame: FrmRefPoint;
}

// the linear part of a graphic's local transform (scale and rotation) plus its
// translation, as pixi's matrix fields.
type LinearMap = {a: number; b: number; c: number; d: number; tx: number; ty: number};

const IDENTITY: LinearMap = {a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0};

// the graphic's current local transform. Reading position/scale/rotation one by one
// would miss the rotation, so take the matrix pixi builds from all of them.
function localMatrix(g: Container): LinearMap {
    g.transform.updateLocalTransform();
    return g.localTransform;
}

export function printPoint(x: number, y: number) {
    return `(${x.toFixed(2)}, ${y.toFixed(2)})`;
}
